import { PLAN_VIEW_COLUMNS } from "./planViewColumnsData.jsx";
import { RESOURCE_VIEW_COLUMNS, RESOURCE_VIEW_FILTER_FIELD_CONFIG } from "./planResourceViewColumnsData.jsx";
import SwivuCellEditor from "../../../bryntum-lib/widget/fields/SwivuCellEditor.jsx";
import bgContrastHelper from "../../utils/bgUtils.jsx";
import {
  cellMenuItemCollapseAllRows,
  cellMenuItemCollapseRow,
  cellMenuItemCopyTo,
  cellMenuItemEditEntity,
  cellMenuItemRemoveRow,
  cellMenuItemSettingsEditor,
  cellMenuItemToggleArchiveRow,
  entityCategorySelector,
  entitySendBoardAlert,
  entityStatusSelector,
  entityTagsSelector,
  entityTeamSelector
} from "../../utils/gridCellMenuUtils.jsx";
import {eventTooltipRenderer} from "../../utils/utils.jsx";
import { SWIVU_GROUP_CONTEXT_MENU_SELECTOR, SWIVU_TOOLTIP_TRIGGER_CLS } from "../OtherKeys.jsx";
import {SCHEDULE_VIEW_FILTER_FIELD_CONFIG} from "../SchedulerView/schedulerViewPlansColumnsData.jsx";
import SwivuCalendarHelper from "../../utils/calendarUtils.jsx";
import {SWIVU_TASK_DEFAULT_COLOR} from "../../configs/globalConfig.jsx";
import { gridHeaderMenuProcessItems } from "../../utils/otherUtils.jsx";
import { DateHelper, StringHelper } from "@bryntum/schedulerpro";

export const PLAN_VIEW_CONFIG = {
  columns: PLAN_VIEW_COLUMNS,
  infiniteScroll: true,
  preserveScrollOnDatasetChange: true,
  barMargin: 0,
  cls: "swivu-plan-grid",
  subGridConfigs: {
    left: {
      weight: 10,
      width: "12.5%"
    },
    locked: {
      weight: 20,
      width: "12.5%"
    },
    normal: {
      weight: 30,
      flex: 1
    }
  },
  startDate: new Date(),
  statefulEvents: ['subGridCollapse', 'subGridExpand', 'horizontalScroll', 'stateChange', 'scroll', 'swivuTaskStoreFilter', 'swivuTaskStoreSort', 'swivuTaskStoreGroup'],
  eventRenderer({eventRecord, resourceRecord, renderData}) {
    if (eventRecord.isPlanningDateEvent) {
      renderData.cls = `${renderData.cls} swivu-plan-date-range-event`
      return [
        {
          tag: "div",
          class: "flex flex-col justify-start align-start",
          children: [
            {
              html: StringHelper.encodeHtml(eventRecord.name)
            }
          ]
        }
      ];
    } else {
      eventRecord.readOnly = true;
      const style = {
        color: resourceRecord.color ? bgContrastHelper(resourceRecord.color, '#fff', '#000') : '#000',
        backgroundColor: resourceRecord.color ?? SWIVU_TASK_DEFAULT_COLOR,
        fontSize: "1em",
        textDecoration: resourceRecord.archived ? "line-through" : "none"
      };

      renderData.style = style;
      return resourceRecord.name;
    }
  },
  viewPreset: "dayAndWeek",
  // Clicking header sorts events now, disabling zooming to not overload header with interactions
  zoomOnTimeAxisDoubleClick: false,
  zoomOnMouseWheel: false,
  enableRecurringEvents: true,
  filterFeature: {
    pickerConfig: {
      fields: SCHEDULE_VIEW_FILTER_FIELD_CONFIG
    },
    closeEmptyPopup: true
  },
  timeRangesFeature: {
    currentDateFormat: "h:mm A",
    showCurrentTimeLine: true,
  },
  taskEditFeature: false,
  dependenciesFeature: false,
  rowCopyPasteFeature: false,
  // Plan view is read only for now and nothing to put on the menu
  eventMenuFeature: {
    items: {
      // Unassign and delete work identically in our case
      copyEvent: false,
      cutEvent: false,
      deleteEvent: false,
      unassignEvent: false,
      splitEvent: false,
      renameSegment: false
    }
  },
  timeAxisHeaderMenuFeature: {
    items: {
      eventsFilter: false,
      dateRange: false,
      currentTimeLine: true
    }
  },
  eventDragFeature: true,
  eventDragCreateFeature: true,
  eventResizeFeature: {
    showExactResizePosition: true,
  },
  fillTicks: true,
  eventTooltipFeature: {
    template: ({ eventRecord, startDate, endDate }) => {
      const format = 'MMM D, YYYY h A';
      return eventTooltipRenderer(
        eventRecord.resource.name, DateHelper.format(startDate, format), DateHelper.format(endDate, format), eventRecord.taskEventResources
      )
    },
    listeners: {
      beforeShow({ source }) {
        // Based on the value of custom property tooltip is rendered
        // Custom property is added/toggle from eventMouseOver listener, See listeners section below.
        return source.eventRecord._swivuShowTooltip;
      },
    },
  },
  // Schedule menu allows to add events, which is not yet supported for the Plan view
  scheduleMenuFeature: {
    items: {
      addEvent: false,
      pasteEvent: false
    }
  },
  timeAxis: {
    listeners: {
      reconfigure(event) {
        SwivuCalendarHelper.reconfigureVisualNonWorkingDaysFromCalendar(event.source.owner);
      }
    }
  },
  listeners: {
    eventMouseOver(e) {
      const { event, eventRecord } = e;
      // This listener toggles a custom property based on whether the shift key is pressed or not, whenever mouse over event is triggered
      eventRecord._swivuShowTooltip = event.shiftKey;
    },
  },
  rowReorderFeature: true,
  cellEditFeature: { editorClass: SwivuCellEditor },
  cellMenuFeature: {
    clickTriggerSelector: SWIVU_GROUP_CONTEXT_MENU_SELECTOR,
    items: {
      cut: false,
      copy: false,
      paste: false,
      filterStringEquals: false,
      filterDisable: false,
      filterMenu: false,
      // revert later, currently plans view is readonly, so we are safe to temporarily hide them
      removeRow: cellMenuItemRemoveRow({ hidden: true }),
      archive: cellMenuItemToggleArchiveRow({ hidden: true }),
      collapseRow: cellMenuItemCollapseRow(),
      collapseAllRows: cellMenuItemCollapseAllRows(),
      settingsEditor: cellMenuItemSettingsEditor()
    },
  },
  headerMenuFeature: {
    processItems: gridHeaderMenuProcessItems("leftRegion", "lockedRegion")
  },
  cellTooltipFeature: {
    minWidth: "35em",
    maxHeight: "40em",
    scrollable: true,
    contentElementCls: "!m-0 !p-[1em]",
    forSelector: `.${SWIVU_TOOLTIP_TRIGGER_CLS}`,
    allowOver: true,
    dismissDelay: 0,
  }
};

export const  RESOURCE_VIEW_CONFIG = {
  columns: RESOURCE_VIEW_COLUMNS,
  cls: "swivu-plan-resources-grid",
  subGridConfigs: {
    locked: {
      weight: 20
    },
    normal: {
      weight: 30
    }
  },
  regionResizeFeature: true,
  filterFeature: {
    pickerConfig: {
      fields: RESOURCE_VIEW_FILTER_FIELD_CONFIG
    },
    closeEmptyPopup: true
  },
  preserveScrollOnDatasetChange: true,
  statefulEvents: ['subGridCollapse', 'subGridExpand', 'horizontalScroll', 'stateChange', 'scroll', 'swivuResourceStoreFilter', 'swivuResourceStoreSort', 'swivuResourceStoreGroup'],
  rowReorderFeature: true,
  cellEditFeature: { editorClass: SwivuCellEditor },
  cellTooltipFeature: {
    tooltipRenderer(e) {
      if (e.column?.swivuTooltip) {
        return e.column?.swivuTooltip(e);
      }
    }
  },
  rowCopyPasteFeature: false,
  cellMenuFeature: {
    clickTriggerSelector: SWIVU_GROUP_CONTEXT_MENU_SELECTOR,
    items: {
      cut: false,
      copy: false,
      paste: false,
      filterStringEquals: false,
      filterDisable: false,
      removeRow: cellMenuItemRemoveRow(),
      archive: cellMenuItemToggleArchiveRow(),
      filterMenu: false,
      // Resource's View Agenda is temporarily disabled
      // viewAgenda: cellMenuItemViewAgenda(),
      editEntity : cellMenuItemEditEntity({ text: "Edit Resource" }),
      collapseRow: cellMenuItemCollapseRow(),
      collapseAllRows: cellMenuItemCollapseAllRows(),
      settingsEditor: cellMenuItemSettingsEditor(),
      entityStatusSelector: entityStatusSelector(),
      entityCategorySelector: entityCategorySelector(),
      entityTagsSelector: entityTagsSelector(),
      entityTeamSelector: entityTeamSelector(),
      entitySendBoardAlert: entitySendBoardAlert(),
      copyTo: cellMenuItemCopyTo(),
    },
    // Note: If you want to implement processItems function then each main view has its implementation in their files, and don't override it here
  },
  headerMenuFeature: {
    processItems: gridHeaderMenuProcessItems("lockedRegion", "normalRegion")
  }
};
